import { DateTime } from "luxon"

const today = new Date()

const formatAMPM = (date, format = 'string') => {
  if (!(date instanceof Date) || isNaN(date)) return ''
  let hours = date.getHours()
  let minutes = date.getMinutes()
  let amPm = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12
  hours = hours ? hours : 12
  minutes = minutes < 10 ? '0' + minutes : minutes
  if (format === 'array') return [hours, minutes, amPm]
  return hours + ':' + minutes + ' ' + amPm
}
const removeTimezoneFromISO = time => {
  if (!time || typeof time !== 'string') return time || ''
  // UTC: "…Z" — strip only the Z (slice(-6) would corrupt the timestamp)
  if (time.endsWith('Z')) return time.slice(0, -1)
  // Offset: "+00:00" / "-06:00"
  if (/[+-]\d{2}:\d{2}$/.test(time)) return time.slice(0, -6)
  return time
}
const removeDateFromDateTimeISO = time => time.split('T')[1]
const clearTimeZone = time => removeDateFromDateTimeISO(removeTimezoneFromISO(time))

const formatDateObj = date => DateTime.fromISO(date)  // DateTime obj
const formatDateToJS = date => DateTime.fromISO(date).toJSDate()  // Js Date 
const formatDateMed = date => DateTime.fromISO(date).toLocaleString(DateTime.DATE_HUGE) // Friday, October 14, 1983
const formatDateMedMed = date => DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED) //  Oct 14, 1983
const formatDateISOShort = date => DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT) //13/05/2022
const formatDateToFull = date => DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_FULL) // Friday, October 14, 1983

const formatDate = date => DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_SHORT) // 4/5/2023
const formatDateToMed = date => DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_MED) // Oct 14, 1983
const formatDateToISOShort = date => DateTime.fromJSDate(date).toISODate() // '1982-05-25'
const formatDateToISO = date => removeTimezoneFromISO(DateTime.fromJSDate(date).toISO()) // '2017-04-22T20:47:05.335'
const formatDateToMedRange = dates => {
  if (!Array.isArray(dates) || !dates[0] || !dates[1]) return ''
  return `${formatDateToMed(dates[0])} - ${formatDateToMed(dates[1])}`
}

const formatTime = time => formatAMPM(DateTime.fromISO(time).toJSDate()) // 11:55 am
const formatTimeFromJS = time => formatAMPM(time) // 11:55 am
const formatTimeFromJSArray = time => formatAMPM(time, 'array') // [11, 55, "am"]
const formatDateToTimeISO = time => clearTimeZone(DateTime.fromJSDate(time).toISO()) // '20:47:05.335'
const formatDateToTimeHour = time => DateTime.fromJSDate(time).hour // '20'
const getCurrentHour = (plusHour = 0, plusMinutes = 0) => {
  return DateTime.local()
    .plus({ hours: plusHour, minutes: plusMinutes })
    .toJSDate()
}
// Ruby Visit#to_json uses '%Y-%m-%d %I:%M:%S %p' (English AM/PM, no offset).
const SERVER_VISIT_TIME_FORMAT = 'yyyy-MM-dd hh:mm:ss a'

const parseVisitDateTime = (value) => {
  if (value == null) return null
  const raw = String(value).trim()
  if (!raw) return null

  const iso = DateTime.fromISO(raw, { setZone: true })
  if (iso.isValid) return iso

  // Locale is pinned to en-US: Settings.defaultLocale is `es` in the app and
  // Luxon then rejects Ruby's "AM"/"PM". Zone is left naive (local/default)
  // so the wall clock from the API is not shifted.
  const server = DateTime.fromFormat(raw, SERVER_VISIT_TIME_FORMAT, { locale: 'en-US' })
  if (server.isValid) return server

  return null
}

const removeDatefromDateTime = (dateTime = "") => {
  if (!dateTime) return ''

  // Server format: "2026-04-21 02:23:42 PM" — literal slice, no Date/Luxon.
  // Re-parsing as UTC (or with locale `es`) would blank the cell or shift 6h.
  const parts = String(dateTime).split(' ')
  if (parts.length === 3) {
    const time = parts[1]?.slice(0, 5) || ''
    return `${time} ${parts[2]}`
  }

  const parsed = DateTime.fromISO(String(dateTime), { setZone: true })
  if (!parsed.isValid) return ''
  return parsed.setZone('local').setLocale('en-US').toFormat('hh:mm a')
}

const formatMultiDateToMed = (dateList = [], t) => {
  let months = []
  let item = {}

  if (dateList.length === 0) return ''

  dateList.forEach(d => {
    const month = d.getMonth()
    const index = months.findIndex(m => m.month === month)
    const date = DateTime.fromJSDate(d)
    if (index === -1) {
      item = {
        month: month,
        monthAndYear: `${t('of')} ${date.monthShort} ${date.year}`,
        days: [date.day]
      }
      months.push(item)
    } else months[index].days.push(date.day)
  })

  return months.map(item => `${item.days.join(", ")} ${item.monthAndYear}`).join(", ")
} // 14,15,16 of Oct 1983, 5 of Nov 1983  

const getAllTime = () => {
  const start = clearTimeZone(DateTime.local().set({ hour: 0, minute: 0, second: 0 }).toISO())
  const end = clearTimeZone(DateTime.local().set({ hour: 23, minute: 59, second: 59 }).toISO())
  return { start, end }
}

const isToday = date => DateTime.fromISO(date).hasSame(DateTime.local(), "day") // true or false

const isTodayOrFuture = date => DateTime.fromISO(date).startOf('day') >= DateTime.local().startOf('day') // true if date is today or later

const isTodayFromJS = date => DateTime.fromJSDate(new Date(date)).hasSame(DateTime.local(), 'day');  // true or false

const isAllDay = ({ entry_time, exit_time }) => {

  const _entry_time = formatDateToJS(entry_time)
  const _exit_time = formatDateToJS(exit_time)

  let entry_hour = _entry_time.getHours()
  let entry_minute = _entry_time.getMinutes()
  let exit_hour = _exit_time.getHours()
  let exit_minute = _exit_time.getMinutes()

  return (
    entry_hour === 0 &&
    entry_minute === 0 &&
    exit_hour === 23 &&
    exit_minute === 59
  )
}

const areDatesEqualFromJS = (date1, date2) => {
  return DateTime.fromJSDate(date1).hasSame(DateTime.fromJSDate(date2), 'day')
}

const getTimeDuration = (entryTime, exitTime) => {
  if (!(entryTime instanceof Date) || !(exitTime instanceof Date)) {
    throw new Error('Parameters must be Date instances')
  }

  let start = new Date(entryTime.getTime())
  let end = new Date(exitTime.getTime())

  if (end <= start) {
    end.setDate(end.getDate() + 1)
  }

  return Math.round((end - start) / 60000)
}

const formatDateAndTimeToISO = (date, time) => {
  if (!(date instanceof Date) || !(time instanceof Date)) {
    throw new Error('Parameters must be Date instances')
  }

  return DateTime.fromJSDate(date)
    .set({
      hour: time.getHours(),
      minute: time.getMinutes(),
      second: time.getSeconds(),
      millisecond: time.getMilliseconds(),
    })
    .toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
}

const getFormateDate = ({ multiDay, date, t }) => {
  if (multiDay && date) return formatDateToMedRange(date)
  if (date) return formatDateToMed(date)
  return t('select')
}

const getFormateLastDate = ({ multiDay, date }) => {
  if (multiDay && Array.isArray(date)) return formatDateToMed(date[1])
  return formatDateToMed(date)
}


export {
  today,
  isToday,
  isTodayOrFuture,
  isTodayFromJS,
  isAllDay,
  getAllTime,
  formatDateObj,
  formatDateMed,
  formatDateMedMed,
  formatDateToJS,
  formatDateISOShort,
  formatMultiDateToMed,
  formatDate,
  formatDateToMed,
  formatDateToISOShort,
  formatDateToISO,
  formatDateToMedRange,
  formatTime,
  formatDateToFull,
  formatTimeFromJS,
  formatDateToTimeHour,
  formatTimeFromJSArray,
  formatDateToTimeISO,
  getCurrentHour,
  getTimeDuration,
  formatDateAndTimeToISO,
  getFormateDate,
  getFormateLastDate,
  removeTimezoneFromISO,
  removeDateFromDateTimeISO,
  removeDatefromDateTime,
  parseVisitDateTime,
  areDatesEqualFromJS
}
